import {
  DAYS_IN_WEEK,
  DRAGGABLE_EVENT_TIME_STEP_IN_MINUTES,
  MINUTES_IN_DAY,
} from '~/Screens/Calendar/constants';
import {
  ICalendarOffset,
  IDraggableEvent,
  IEventPosition,
  IEventTimeInSeconds,
} from '~/Screens/Calendar/interfaces';
import { TIME_VALUES } from '~/Constants/Date.constants';
import { startOfDay } from 'date-fns';
import { IEvent } from '~/Models/DTO/Event.model';
import { XYCoord } from 'react-dnd';

export enum DraggableType {
  EVENT = 'event',
  FULL_DAY_ACTIVITY = 'full day activity',
}

export const snapToGrid = ({
  position,
  container,
}: {
  position: { x?: number; y: number };
  container: { width: number; height: number };
}) => {
  const x = position.x ? Math.round(position.x / container.width) * container.width : 0;
  const y = Math.round(position.y / container.height) * container.height;

  return { x, y };
};

export const calculateDraggableLayerSizes = ({ offset }: { offset: ICalendarOffset }) => {
  const width = offset.width;
  const height = offset.height / (MINUTES_IN_DAY / DRAGGABLE_EVENT_TIME_STEP_IN_MINUTES);

  return { width, height };
};

export interface ICalculateEventPosition {
  item: IDraggableEvent;
  offset: ICalendarOffset;
  differenceFromInitialOffset: XYCoord;
}

export type IGetEventPosition = (props: Omit<ICalculateEventPosition, 'offset'>) => IEventPosition;

export const calculateEventPositionForWeek = ({
  item: {
    position: { daysOffset, top, left, width: baseWidth, height },
  },
  offset,
  differenceFromInitialOffset,
}: ICalculateEventPosition) => {
  const dayWidth = offset.width;
  const shift = dayWidth * daysOffset;
  const container = calculateDraggableLayerSizes({
    offset: { ...offset, width: dayWidth * DAYS_IN_WEEK },
  });
  const position = snapToGrid({
    container: { height: container.height, width: container.width / DAYS_IN_WEEK },
    position: {
      x: shift + differenceFromInitialOffset.x - Math.abs(dayWidth - baseWidth) / 2 + left,
      y: top + differenceFromInitialOffset.y,
    },
  });

  return {
    top: position.y,
    left: position.x,
    width: dayWidth,
    height,
    daysOffset: Math.round((position.x - shift) / dayWidth),
  };
};

export const calculateEventPositionForDay = ({
  item: {
    position: { top, width, left, height },
  },
  offset,
  differenceFromInitialOffset,
}: ICalculateEventPosition) => {
  const container = calculateDraggableLayerSizes({ offset });
  const position = snapToGrid({
    container,
    position: { y: top + differenceFromInitialOffset.y },
  });

  return {
    top: position.y,
    left: differenceFromInitialOffset.x + left,
    width,
    height,
    daysOffset: 0,
  };
};

export const getEventTimeInSeconds = (event: {
  end: string;
  start: string;
}): IEventTimeInSeconds => {
  const end = Date.parse(event?.end);
  const start = Date.parse(event?.start);

  return { start, end, diff: end - start, dayStart: Number(startOfDay(start)) };
};

export interface ICalculateNewEvent {
  time: IEventTimeInSeconds;
  event: IEvent;
  position: IEventPosition;
  container: ICalendarOffset;
}

export const calculateNewEvent = ({
  time,
  event,
  position,
  container,
}: ICalculateNewEvent): IEvent => {
  const start =
    time.dayStart +
    position.daysOffset * TIME_VALUES.DAY +
    TIME_VALUES.DAY * (position.top / container.height);

  return {
    ...event,
    end: new Date(start + time.diff).toISOString(),
    start: new Date(start).toISOString(),
  };
};

export interface ICalculateFullDayActivity {
  time: IEventTimeInSeconds;
  event: IEvent;
  position: IEventPosition;
}

export const calculateFullDayActivity = ({
  time,
  event,
  position,
}: ICalculateFullDayActivity): IEvent => {
  const start = time.start + position.daysOffset * TIME_VALUES.DAY;

  return {
    ...event,
    end: new Date(start + time.diff).toISOString(),
    start: new Date(start).toISOString(),
  };
};
